// ============================================================
// SampleDataCard.js — trial sample data (2026-10-20)
// docs/architecture/trial-sample-data.md
//
// Shows whether this client has sample data, adds it (trial or demo
// accounts only), and removes it. Removal shows first what the customer has
// added to sample people: those records are deleted only when the operator
// ticks to include them; otherwise they and the sample people they belong to
// are kept. Confirmation is in the page (never window.confirm, Gotcha #147).
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from '../../components/Toaster';
import { getClientSampleData, addClientSampleData, removeClientSampleData } from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

function CountList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="pc-sample-list">
      {items.map((i) => <li key={i.table}>{i.label}: {i.count}</li>)}
    </ul>
  );
}

export default function SampleDataCard({ clientId }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'add' | 'remove' | null
  const [error, setError] = useState(null);
  const [includeCustomer, setIncludeCustomer] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPreview(await getClientSampleData(clientId));
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  async function add() {
    setBusy('add');
    setError(null);
    try {
      await addClientSampleData(clientId);
      toast.success('Sample data added');
      await load();
    } catch (err) {
      setError(translateError(err));
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    setBusy('remove');
    setError(null);
    try {
      const res = await removeClientSampleData(clientId, includeCustomer);
      toast.success(res.keptTotal > 0 ? `Sample data removed; ${res.keptTotal} kept` : 'Sample data removed');
      setConfirming(false);
      setIncludeCustomer(false);
      await load();
    } catch (err) {
      setError(translateError(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading && !preview) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;

  const present = preview && preview.present;
  const added = preview ? preview.customerAddedTotal || 0 : 0;

  return (
    <div className="pc-sample">
      <p className="pc-explain">
        Sample data gives a trial account a few people receiving care, staff, CareTrack events, policies and
        training records to try the product on. Sample staff can never sign in, are never billed and never
        receive emails. Removing it deletes only the sample records.
      </p>

      {error && <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>}
      {busy === 'add' && (
        <StatusMessage as="p" variant="loading" className="pc-state pc-sample-wait">
          Adding the sample data. This takes a few seconds.
        </StatusMessage>
      )}
      {busy === 'remove' && (
        <StatusMessage as="p" variant="loading" className="pc-state pc-sample-wait">
          Removing the sample data. This usually takes 10 to 30 seconds, so please keep this page open.
        </StatusMessage>
      )}

      {!present && (
        <>
          <StatusMessage as="p" variant="empty" className="pc-state">This account has no sample data.</StatusMessage>
          <div className="pc-actions">
            <button type="button" className="pc-btn-primary" onClick={add} disabled={busy !== null}>
              {busy === 'add' ? 'Adding…' : 'Add sample data'}
            </button>
          </div>
        </>
      )}

      {present && (
        <>
          <p className="pc-state">This account has {preview.sampleTotal} sample {preview.sampleTotal === 1 ? 'record' : 'records'}:</p>
          <CountList items={preview.sample} />

          {added > 0 && (
            <div className="pc-sample-added">
              <p className="pc-state">
                The customer has added {added} {added === 1 ? 'record' : 'records'} to sample people or items:
              </p>
              <CountList items={preview.customerAdded} />
              <label className="pc-checkbox">
                <input
                  type="checkbox"
                  checked={includeCustomer}
                  onChange={(e) => setIncludeCustomer(e.target.checked)}
                  disabled={busy !== null}
                />{' '}
                Also delete the {added === 1 ? 'record' : `${added} records`} the customer added
              </label>
              {!includeCustomer && (
                <p className="pc-explain">
                  If this is not ticked, those records are kept, along with the sample records they belong to.
                </p>
              )}
            </div>
          )}

          <div className="pc-actions">
            {!confirming && (
              <button type="button" className="pc-btn-secondary" onClick={() => setConfirming(true)} disabled={busy !== null}>
                Remove sample data
              </button>
            )}
            {confirming && (
              <>
                <span className="pc-state">This cannot be undone.</span>
                <button type="button" className="pc-btn-primary" onClick={remove} disabled={busy !== null}>
                  {busy === 'remove' ? 'Removing…' : 'Yes, remove it'}
                </button>
                <button type="button" className="pc-btn-secondary" onClick={() => setConfirming(false)} disabled={busy !== null}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
